import { create } from 'zustand';
import type { User, Wallet, PoiCategory, ZonesStatus } from './lib/api';
import {
  DEFAULT_SHORTCUTS,
  assignShortcut,
  loadShortcuts,
  saveShortcuts,
  type ShortcutAction,
  type ShortcutMap,
} from './lib/keyboardShortcuts';

export type Panel = 'none' | 'auth' | 'territory' | 'leaderboard' | 'profile' | 'shortcuts' | 'help' | 'empire' | 'alliances' | 'admin' | 'poi' | 'resources' | 'technologies' | 'chat' | 'buildings' | 'suggestions' | 'onu' | 'market';

export type FlyToRequest = {
  lng: number;
  lat: number;
  zoom?: number;
  territoryId?: number;
  openTerritory?: boolean;
  seq: number;
};

type AppState = {
  user: User | null;
  wallet: Wallet | null;
  zones: ZonesStatus | null;
  selectedTerritoryId: number | null;
  /** Territoires adjacents illuminés sur la carte (toggle panneau). */
  neighborHighlightIds: number[];
  /** Aura de portée d’un port sélectionné (cercle rouge sur la carte). */
  portAura: { territoryId: number; lng: number; lat: number; rangeKm: number } | null;
  selectedPoiId: number | null;
  panel: Panel;
  /** Sheet mobile agrandi en plein écran (reset à chaque changement de panneau). */
  mobileSheetExpanded: boolean;
  setMobileSheetExpanded: (v: boolean) => void;
  /** Vue Bâtiments : empire (liste complète) ou territoire (récap + construction locale). */
  buildingsScope: 'empire' | 'territory';
  /** Peer ouvert dans le panneau Chat (depuis nav ou territoire). */
  chatPeerUserId: number | null;
  /** Alerte Bourse à ouvrir dans l’onglet admin (depuis AdminPanel). */
  marketAdminFlagId: number | null;
  /** Catégories POI visibles sur la carte (null = toutes) */
  poiCategories: Set<PoiCategory> | null;
  setPoiCategories: (s: Set<PoiCategory> | null) => void;
  setSelectedPoi: (id: number | null) => void;
  openMarketRiskFlag: (flagId: number) => void;
  clearMarketAdminFlag: () => void;
  conquering: boolean;
  lastReveal: { name: string; points: number } | null;
  /** Bumped when empire colors change so the map refreshes paints without full reload. */
  mapPaintRevision: number;
  /**
   * Peinture optimiste d’un territoire (invest vierge / recover vide) avant le refetch geojson.
   * seq incrémenté à chaque claim pour rejouer l’effet MapView.
   * `clear: true` retire claimFill (couleur empire qui sinon reste collée).
   */
  mapColorClaim: {
    territoryId: number;
    fill: string;
    governorUserId: number;
    seq: number;
    clear?: boolean;
  } | null;
  flyToRequest: FlyToRequest | null;
  /** Forced open (e.g. « Revoir le guide ») even if tutorialSeen. */
  tutorialOpen: boolean;
  /** Forced open (ex. bouton Profil) même si déjà vues. */
  newsOpen: boolean;
  /** UI elements lifted above the tutorial blur. */
  tutorialHighlight: null | 'charges' | 'nav';
  /** Mode carte seule : masque le HUD et tous les calques GeoLadder. */
  interfaceHidden: boolean;
  setInterfaceHidden: (v: boolean) => void;
  /** Affichage des drapeaux d'empire sur la carte (on par défaut, mémorisé). */
  empireFlagsVisible: boolean;
  setEmpireFlagsVisible: (v: boolean) => void;
  setUser: (u: User | null) => void;
  setWallet: (w: Wallet | null) => void;
  setZones: (z: ZonesStatus | null) => void;
  setSelected: (id: number | null) => void;
  setNeighborHighlight: (ids: number[]) => void;
  setPortAura: (aura: AppState['portAura']) => void;
  setPanel: (p: Panel) => void;
  togglePanel: (p: Exclude<Panel, 'none'>) => void;
  openBuildings: (scope?: 'empire' | 'territory') => void;
  openChatWith: (userId: number) => void;
  clearChatPeer: () => void;
  setConquering: (v: boolean) => void;
  setLastReveal: (v: AppState['lastReveal']) => void;
  bumpMapPaint: () => void;
  /** Colorie immédiatement un territoire sur la carte (gouvernorat acquis sans adversaire). */
  claimMapTerritoryColor: (territoryId: number, fill: string, governorUserId: number) => void;
  /** Retire la couleur empire optimiste (recover / abandon sans charge restante). */
  clearMapTerritoryColor: (territoryId: number) => void;
  requestFlyTo: (opts: Omit<FlyToRequest, 'seq'>) => void;
  /** Alias de requestFlyTo pour usage dans PoiPanel etc. */
  flyTo: (opts: Omit<FlyToRequest, 'seq'>) => void;
  setTutorialOpen: (v: boolean) => void;
  setTutorialHighlight: (v: AppState['tutorialHighlight']) => void;
  setNewsOpen: (v: boolean) => void;
  shortcuts: ShortcutMap;
  setShortcutKey: (action: ShortcutAction, key: string) => void;
  resetShortcuts: () => void;
};

export const useApp = create<AppState>((set) => ({
  user: null,
  wallet: null,
  zones: null,
  selectedTerritoryId: null,
  neighborHighlightIds: [],
  portAura: null,
  selectedPoiId: null,
  panel: 'none',
  mobileSheetExpanded: false,
  setMobileSheetExpanded: (mobileSheetExpanded) => set({ mobileSheetExpanded }),
  buildingsScope: 'empire',
  chatPeerUserId: null,
  marketAdminFlagId: null,
  poiCategories: null,
  setPoiCategories: (poiCategories) => set({ poiCategories }),
  setSelectedPoi: (selectedPoiId) => set({ selectedPoiId }),
  openMarketRiskFlag: (flagId) =>
    set({ panel: 'market', marketAdminFlagId: flagId, mobileSheetExpanded: false }),
  clearMarketAdminFlag: () => set({ marketAdminFlagId: null }),
  conquering: false,
  lastReveal: null,
  mapPaintRevision: 0,
  mapColorClaim: null,
  flyToRequest: null,
  tutorialOpen: false,
  newsOpen: false,
  tutorialHighlight: null,
  interfaceHidden: false,
  setInterfaceHidden: (interfaceHidden) => set({ interfaceHidden }),
  empireFlagsVisible: (() => {
    try {
      const raw = localStorage.getItem('terra.empireFlagsVisible');
      if (raw === '0') return false;
      if (raw === '1') return true;
    } catch {
      /* ignore */
    }
    return true; // défaut : affichés
  })(),
  setEmpireFlagsVisible: (empireFlagsVisible) => {
    try {
      localStorage.setItem('terra.empireFlagsVisible', empireFlagsVisible ? '1' : '0');
    } catch {
      /* ignore */
    }
    set({ empireFlagsVisible });
  },
  setUser: (user) => set({ user }),
  setWallet: (wallet) => set({ wallet }),
  setZones: (zones) => set({ zones }),
  setSelected: (selectedTerritoryId) =>
    set({ selectedTerritoryId, neighborHighlightIds: [], portAura: null }),
  setNeighborHighlight: (neighborHighlightIds) => set({ neighborHighlightIds }),
  setPortAura: (portAura) => set({ portAura }),
  setPanel: (panel) => set({ panel, mobileSheetExpanded: false }),
  togglePanel: (p) =>
    set((s) => {
      if (s.panel === p) return { panel: 'none', mobileSheetExpanded: false };
      if (p === 'buildings') {
        return { panel: 'buildings', buildingsScope: 'empire', mobileSheetExpanded: false };
      }
      return { panel: p, mobileSheetExpanded: false };
    }),
  openBuildings: (scope = 'empire') =>
    set({ panel: 'buildings', buildingsScope: scope, mobileSheetExpanded: false }),
  openChatWith: (userId) =>
    set({ panel: 'chat', chatPeerUserId: userId, mobileSheetExpanded: false }),
  clearChatPeer: () => set({ chatPeerUserId: null }),
  setConquering: (conquering) => set({ conquering }),
  setLastReveal: (lastReveal) => set({ lastReveal }),
  bumpMapPaint: () => set((s) => ({ mapPaintRevision: s.mapPaintRevision + 1 })),
  claimMapTerritoryColor: (territoryId, fill, governorUserId) =>
    set((s) => ({
      mapColorClaim: {
        territoryId,
        fill,
        governorUserId,
        seq: (s.mapColorClaim?.seq ?? 0) + 1,
        clear: false,
      },
      mapPaintRevision: s.mapPaintRevision + 1,
    })),
  clearMapTerritoryColor: (territoryId) =>
    set((s) => ({
      mapColorClaim: {
        territoryId,
        fill: '#2A3548',
        governorUserId: 0,
        seq: (s.mapColorClaim?.seq ?? 0) + 1,
        clear: true,
      },
      mapPaintRevision: s.mapPaintRevision + 1,
    })),
  requestFlyTo: (opts) =>
    set((s) => ({
      flyToRequest: { ...opts, seq: (s.flyToRequest?.seq ?? 0) + 1 },
    })),
  flyTo: (opts) =>
    set((s) => ({
      flyToRequest: { ...opts, seq: (s.flyToRequest?.seq ?? 0) + 1 },
    })),
  setTutorialOpen: (tutorialOpen) =>
    set({ tutorialOpen, tutorialHighlight: tutorialOpen ? null : null }),
  setTutorialHighlight: (tutorialHighlight) => set({ tutorialHighlight }),
  setNewsOpen: (newsOpen) => set({ newsOpen }),
  shortcuts: typeof window !== 'undefined' ? loadShortcuts() : { ...DEFAULT_SHORTCUTS },
  setShortcutKey: (action, key) =>
    set((s) => {
      const shortcuts = assignShortcut(s.shortcuts, action, key);
      saveShortcuts(shortcuts);
      return { shortcuts };
    }),
  resetShortcuts: () => {
    const shortcuts = { ...DEFAULT_SHORTCUTS };
    saveShortcuts(shortcuts);
    set({ shortcuts });
  },
}));
